
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';

interface BusinessHoursProps {
  restaurantId: string;
}

interface DayHours {
  isOpen: boolean;
  openTime: string;
  closeTime: string;
}

interface WeeklyHours {
  monday: DayHours;
  tuesday: DayHours;
  wednesday: DayHours;
  thursday: DayHours;
  friday: DayHours;
  saturday: DayHours;
  sunday: DayHours;
}

const BusinessHours = ({ restaurantId }: BusinessHoursProps) => {
  const [hours, setHours] = useState<WeeklyHours>({
    monday: { isOpen: true, openTime: '09:00', closeTime: '21:00' },
    tuesday: { isOpen: true, openTime: '09:00', closeTime: '21:00' },
    wednesday: { isOpen: true, openTime: '09:00', closeTime: '21:00' },
    thursday: { isOpen: true, openTime: '09:00', closeTime: '21:00' },
    friday: { isOpen: true, openTime: '09:00', closeTime: '21:00' },
    saturday: { isOpen: true, openTime: '09:00', closeTime: '21:00' },
    sunday: { isOpen: false, openTime: '09:00', closeTime: '21:00' }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();

  const daysOfWeek = [
    'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
  ];

  // Get current time in Gambia timezone for display
  const getCurrentGambiaTime = () => {
    const now = new Date();
    return now.toLocaleString('en-US', { 
      timeZone: 'Africa/Banjul',
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  useEffect(() => {
    fetchBusinessHours();
  }, [restaurantId]);

  const fetchBusinessHours = async () => {
    try {
      console.log('Fetching business hours for restaurant:', restaurantId);
      
      const { data, error } = await supabase
        .from('restaurants')
        .select('opening_hours')
        .eq('id', restaurantId)
        .single();

      if (error) {
        console.error('Error fetching business hours:', error);
        throw error;
      }

      console.log('Fetched business hours data:', data);

      if (data?.opening_hours) {
        const savedHours = data.opening_hours as unknown;
        if (savedHours && typeof savedHours === 'object') {
          console.log('Setting hours from database:', savedHours);
          setHours(savedHours as WeeklyHours);
        }
      }
    } catch (error) {
      console.error('Error fetching business hours:', error);
      toast({
        title: t('error'),
        description: "Failed to load business hours",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateDayHours = (day: string, field: keyof DayHours, value: boolean | string) => {
    setHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day as keyof WeeklyHours],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      console.log('Saving business hours:', hours);
      
      const hoursJson = JSON.parse(JSON.stringify(hours));
      
      const { error: restaurantError } = await supabase
        .from('restaurants')
        .update({ 
          opening_hours: hoursJson,
          updated_at: new Date().toISOString()
        })
        .eq('id', restaurantId);

      if (restaurantError) {
        console.error('Error saving restaurant hours:', restaurantError);
        throw restaurantError;
      }

      try {
        const { error: appSettingsError } = await supabase
          .from('app_settings')
          .upsert({
            key: 'business_hours',
            value: JSON.stringify(hoursJson),
            description: 'Default business hours for all restaurants',
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'key'
          });

        if (appSettingsError) {
          console.error('Error updating app settings:', appSettingsError);
        }
      } catch (appError) {
        console.error('Error updating global business hours:', appError);
      }

      console.log('Business hours saved successfully');
      toast({
        title: t('success'),
        description: "Business hours updated successfully",
      });
    } catch (error) {
      console.error('Error saving business hours:', error);
      toast({
        title: t('error'),
        description: "Failed to save business hours",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const copyToAllDays = (sourceDay: string) => {
    const sourceDayHours = hours[sourceDay as keyof WeeklyHours];
    const newHours = { ...hours };
    
    daysOfWeek.forEach(day => {
      if (day !== sourceDay) {
        newHours[day as keyof WeeklyHours] = { ...sourceDayHours };
      }
    });
    
    setHours(newHours);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('business_hours')}</CardTitle>
        <div className="text-sm text-gray-600">
          Current time in Gambia: {getCurrentGambiaTime()}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {daysOfWeek.map((day) => {
          const dayHours = hours[day as keyof WeeklyHours];
          const dayName = t(day);
          
          return (
            <div key={day} className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Switch
                    checked={dayHours.isOpen}
                    onCheckedChange={(checked) => updateDayHours(day, 'isOpen', checked)}
                  />
                  <Label className="text-base font-medium min-w-[100px]">
                    {dayName}
                  </Label>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToAllDays(day)}
                  disabled={!dayHours.isOpen}
                >
                  {t('copy_to_all')}
                </Button>
              </div>
              
              {dayHours.isOpen && (
                <div className="grid grid-cols-2 gap-4 ml-12">
                  <div>
                    <Label htmlFor={`${day}-open`} className="text-sm">{t('open_time')}</Label>
                    <Input
                      id={`${day}-open`}
                      type="time"
                      value={dayHours.openTime}
                      onChange={(e) => updateDayHours(day, 'openTime', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`${day}-close`} className="text-sm">{t('close_time')}</Label>
                    <Input
                      id={`${day}-close`}
                      type="time"
                      value={dayHours.closeTime}
                      onChange={(e) => updateDayHours(day, 'closeTime', e.target.value)}
                    />
                  </div>
                </div>
              )}
              
              {!dayHours.isOpen && (
                <div className="ml-12 text-sm text-gray-500">
                  {t('closed')}
                </div>
              )}
            </div>
          );
        })}
        
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="w-full"
        >
          {saving ? t('saving') : t('save_business_hours')}
        </Button>
      </CardContent>
    </Card>
  );
};

export default BusinessHours;
